import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "@lib/firebase";
import { doc, getDoc, updateDoc, collection, getDocs, query, where, limit } from "firebase/firestore";
import { useNotification } from "../../common/NotificationContext";
import { InputField } from "@cakrawalajakarta/shared-components";
import { 
    LuArrowLeft, 
    LuTicket, 
    LuPercent, 
    LuUsers, 
    LuSave,
    LuLoader
} from "react-icons/lu";

export const VoucherDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [categories, setCategories] = useState([]);
  
  const [usageHistory, setUsageHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [formData, setFormData] = useState({
    discountType: "percentage",
    discountValue: "",
    allowedCategory: "",
    isActive: true,
    maxUsage: "",
    expiredAt: "" 
  });

  useEffect(() => {
    fetchVoucherAndHistory();
    fetchCategories();
  }, [id, navigate]);

  const fetchCategories = async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", "JOTR2026"));
      if (eventDoc.exists()) {
        const data = eventDoc.data();
        if (data.categories && Array.isArray(data.categories)) {
          setCategories(data.categories.map(c => c.categoryName));
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchVoucherAndHistory = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, "vouchers", id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setVoucher({ id: docSnap.id, ...data });
        
        const discountType = data.discountType || "percentage";
        const discountValue = data.discountValue || data.discountPercentage || "";
        const allowedCategory = data.allowedCategory || "";
        
        setFormData({
          discountType,
          discountValue,
          allowedCategory,
          isActive: data.isActive,
          maxUsage: data.maxUsage || "",
          expiredAt: data.expiredAt || ""
        });

        let historyData = [];

        try {
          const usedByRef = collection(db, "vouchers", id, "usedBy");
          const usedBySnapshot = await getDocs(usedByRef);
          if (!usedBySnapshot.empty) {
            historyData = usedBySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          }
        } catch (err) {
          console.error("Subcollection fetch failed:", err);
        }

        if (historyData.length === 0 && data.usedBy) {
          if (Array.isArray(data.usedBy)) {
            historyData = data.usedBy;
          } else if (typeof data.usedBy === 'object') {
            historyData = Object.values(data.usedBy);
          }
        }

        if (historyData.length > 0) {
          const uidToRegistration = new Map();

          const missingUids = Array.from(
            new Set(
              historyData
                .filter((r) => r?.uid && (!r.orderId || r.category === "Unknown" || !r.category))
                .map((r) => r.uid)
            )
          );

          if (missingUids.length > 0) {
            await Promise.all(
              missingUids.map(async (uid) => {
                try {
                  const regRef = collection(db, "pendaftaranJOTR2026");
                  const regQuery = query(regRef, where("uid", "==", uid), limit(1));
                  const regSnap = await getDocs(regQuery);
                  if (!regSnap.empty) {
                    const regDoc = regSnap.docs[0];
                    uidToRegistration.set(uid, { id: regDoc.id, ...regDoc.data() });
                  }
                } catch (e) {
                  console.error("Registration lookup failed:", e);
                }
              })
            );
          }

          const enriched = historyData.map((r) => {
            if (!r?.uid) return r;
            const reg = uidToRegistration.get(r.uid);
            if (!reg) return r;

            const categoryFromReg = reg.distance ? `JOTR ${reg.distance}K` : reg.kategori;
            return {
              ...r,
              orderId: r.orderId || reg.orderId,
              category: (!r.category || r.category === "Unknown") ? (categoryFromReg || r.category) : r.category,
              eventId: r.eventId || reg.eventId,
            };
          });

          const history = [...enriched].sort((a, b) => {
            const getTime = (t) => {
              if (!t) return 0;
              if (t.seconds) return t.seconds * 1000;
              if (t instanceof Date) return t.getTime();
              if (typeof t === 'string') return new Date(t).getTime();
              return 0;
            };
            return getTime(b.usedAt) - getTime(a.usedAt);
          });
          setUsageHistory(history);
        } else {
          setUsageHistory([]);
        }
      } else {
        showNotification("error", "Voucher not found");
        navigate("/admin/vouchers");
        return;
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      showNotification("error", "Failed to load voucher details");
    } finally {
      setLoading(false);
      setLoadingHistory(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      const voucherData = {
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue),
        discountPercentage: formData.discountType === "percentage" ? Number(formData.discountValue) : 0,
        allowedCategory: formData.allowedCategory || null,
        isActive: formData.isActive,
        maxUsage: formData.maxUsage ? Number(formData.maxUsage) : null,
        expiredAt: formData.expiredAt
      };

      await updateDoc(doc(db, "vouchers", id), voucherData);
      showNotification("success", "Voucher updated successfully");
      setVoucher(prev => ({ ...prev, ...voucherData }));
    } catch (error) {
      console.error("Error updating voucher:", error);
      showNotification("error", "Failed to update voucher");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !voucher) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-slate-800" />
      </div>
    );
  }

  const formatUsedAt = (usedAt) => {
    if (!usedAt) return "-";
    const date = usedAt?.seconds
      ? new Date(usedAt.seconds * 1000)
      : usedAt instanceof Date
        ? usedAt
        : new Date(usedAt);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const discountDisplay = voucher.discountType === "fixed" 
    ? `Rp ${(voucher.discountValue || 0).toLocaleString('en-US')}`
    : `${voucher.discountValue || voucher.discountPercentage || 0}%`;

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin/vouchers")}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-slate-600"
          >
            <LuArrowLeft size={18} />
          </button>
          <h1 className="text-2xl font-bold text-slate-900">Voucher Details</h1>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Voucher Code</p>
              <p className="text-xl font-bold text-slate-800">{voucher.code}</p>
            </div>
            <LuTicket size={24} className="text-slate-300" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Discount</p>
              <p className="text-xl font-bold text-slate-800">{discountDisplay}</p>
            </div>
            <LuPercent size={24} className="text-slate-300" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Usage</p>
              <p className="text-xl font-bold text-slate-800">
                {voucher.usageCount || 0} <span className="text-slate-400 font-normal">/ {voucher.maxUsage || "∞"}</span>
              </p>
            </div>
            <LuUsers size={24} className="text-slate-300" />
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <form onSubmit={handleUpdate} className="bg-white rounded-xl border border-slate-200 p-4">
        {/* Read-only Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pb-4 border-b border-slate-100">
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">Document ID</p>
            <p className="text-slate-700 text-sm">{voucher.id}</p>
          </div>
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">Created At</p>
            <p className="text-slate-800 font-medium">
              {voucher.createdAt 
                ? new Date(voucher.createdAt.seconds ? voucher.createdAt.seconds * 1000 : voucher.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                : "-"}
            </p>
          </div>
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">Current Status</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${voucher.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {voucher.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Editable Fields */}
        <div className="space-y-4">
          {/* Row 1: Discount Type | Discount Value */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              type="select"
              label="Discount Type"
              name="discountType"
              value={formData.discountType}
              onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
              options={[
                { value: "percentage", label: "Percentage (%)" },
                { value: "fixed", label: "Fixed Amount (IDR)" }
              ]}
              labelColor="text-slate-700"
            />
            <InputField
              type="number"
              label="Discount Value"
              name="discountValue"
              min="0"
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
              placeholder={formData.discountType === "percentage" ? "20" : "50000"}
              labelColor="text-slate-700"
            />
          </div>

          {/* Row 2: Max Usage | Expiry Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              type="number"
              label="Max Usage"
              name="maxUsage"
              min="1"
              value={formData.maxUsage}
              onChange={(e) => setFormData({ ...formData, maxUsage: e.target.value })}
              placeholder="Leave empty for unlimited"
              labelColor="text-slate-700"
            />
            <InputField
              type="date"
              label="Expiry Date"
              name="expiredAt"
              value={formData.expiredAt}
              onChange={(e) => setFormData({ ...formData, expiredAt: e.target.value })}
              labelColor="text-slate-700"
            />
          </div>

          {/* Row 3: Allowed Categories | Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              type="select"
              label="Allowed Category"
              name="allowedCategory"
              value={formData.allowedCategory}
              onChange={(e) => setFormData({ ...formData, allowedCategory: e.target.value })}
              options={[
                { value: "", label: "All Categories" },
                ...categories.map(cat => ({ value: cat, label: cat }))
              ]}
              labelColor="text-slate-700"
            />
            <InputField
              type="select"
              label="Status"
              name="isActive"
              value={formData.isActive ? "active" : "inactive"}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.value === "active" })}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" }
              ]}
              labelColor="text-slate-700"
            />
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-900 font-medium transition-colors flex items-center gap-2"
            >
              {saving ? <LuLoader className="animate-spin" size={16} /> : <LuSave size={16} />}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </form>

      {/* Usage History */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-bold text-slate-800">Usage History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Event</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Date Redeemed</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Registration ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadingHistory ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-slate-500">Loading history...</td>
                </tr>
              ) : usageHistory.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-slate-500">No usage history found.</td>
                </tr>
              ) : (
                usageHistory.map((record, index) => {
                  const userName = record.namaLengkap || record.name || "-";
                  const userEmail = record.email || "-";
                  const eventLabel = record.eventName || record.category || record.kategori || record.eventId || "-";
                  const registrationId = record.orderId || record.registrationId || record.order_id || "-";

                  return (
                    <tr key={record.id || `${record.uid || "row"}-${index}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800 text-sm">{userName}</div>
                        <div className="text-xs text-slate-500 truncate">{userEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-sm font-medium">{eventLabel}</td>
                      <td className="px-4 py-3 text-slate-600 text-sm">{formatUsedAt(record.usedAt)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-600 text-xs">{registrationId}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
